import { ref } from 'vue';
import { useRouter } from 'vue-router';
import { useModal, useModalSlot, VueFinalModal } from 'vue-final-modal';
import { useBulkDelete, useCustomToast } from '@/shared/lib';
import {
  deleteRecord,
  deleteRecords,
  getRecordControl,
  listRecordChangelog,
  updateRecordStatus,
} from '../api/records.api';
import EditRecordHomologationModal from '../ui/EditRecordHomologationModal.vue';
import { RecordControlModal, RecordHistoryModal } from '@/widgets/record-modals';
import type {
  RecordChangelogEntry,
  RecordControl,
  RecordModalTarget,
} from '@/widgets/record-modals';
import { buildRecordDeleteTitle } from '../lib/record-delete-confirmation';
import type { RecordDeleteSelection } from '../lib/record-delete-confirmation';

const MODAL_CENTERED_CLASS = 'flex justify-center items-center';

/**
 * Attributes of a centred dialog of this screen. The name is given to the dialog
 * itself because the visible title lives inside the slot and is not associated with
 * the `role="dialog"` the shared modal renders: without it a screen reader
 * announces nothing but "dialog". It is built here, and not inline, because an aria
 * attribute is not part of the declared props of the shared modal even though Vue
 * passes it through to the element.
 */
const buildDialogAttrs = (ariaLabel: string) => {
  const attrs = {
    class: MODAL_CENTERED_CLASS,
    'aria-label': ariaLabel,
    overlayTransition: 'vfm-fade',
    contentTransition: 'vfm-fade',
  };

  return attrs;
};

const EMPTY_TARGET: RecordModalTarget = { id: 0, code: '', title: null, revision: 0 };

const EMPTY_CONTROL: RecordControl = {
  id: 0,
  code: '',
  title: null,
  revision: null,
  elaboration: { memberId: null, memberName: null, date: null },
  approval: null,
  verification: null,
  effectiveDate: null,
};

/**
 * One message for both directions. The switch has already moved by the time it shows, so what the
 * user needs to read is that the change did not stick, not which way it was going.
 */
const RECORD_STATUS_ERROR_MESSAGE =
  'Não foi possível alterar o status do registro. Tente novamente.';

const RECORD_HISTORY_ERROR_MESSAGE =
  'Não foi possível carregar o histórico deste registro. Tente novamente.';

const RECORD_CONTROL_ERROR_MESSAGE =
  'Não foi possível carregar o controle do registro. Tente novamente.';

export interface RecordListActionsOptions {
  selectedRecordIds: () => number[];
  selectedRevisionIds: () => number[];
  refresh: () => Promise<void>;
  /** Moves the row before the request and hands back the undo for a write that fails. */
  applyRecordStatus: (id: number, isActive: boolean) => () => void;
  /** Reconciles what the row alone cannot: its expansion, and the filter it may no longer match. */
  settleRecordStatus: (id: number) => void;
}

/**
 * Actions of the record list that open something: the row menu, the homologation
 * form, the two read only modals and the two deletion confirmations. Opening a
 * modal is not listing state, so it lives apart from `useRecordList`, and the row
 * menu is owned here as well: with several rows expanded there would be one
 * dropdown per revision, and the key of the open one is what keeps a single menu
 * open.
 */
export function useRecordListActions(options: RecordListActionsOptions) {
  const toast = useCustomToast();
  const router = useRouter();
  const openMenuKey = ref<string | null>(null);

  const setOpenMenuKey = (key: string | null) => {
    openMenuKey.value = key;
  };

  const closeMenu = () => {
    openMenuKey.value = null;
  };

  const buildHomologationSlot = (target: RecordModalTarget) =>
    useModalSlot({
      component: EditRecordHomologationModal,
      attrs: {
        target,
        onClose: () => {
          homologationModal.close();
        },
        // The listing renders the vigência the form can change, so a save that
        // did not refresh would leave the row disagreeing with the modal.
        onSaved: () => {
          options.refresh().catch((error: unknown) => {
            console.error('Erro ao atualizar a listagem após a edição:', error);
          });
        },
      },
    });

  const homologationModal = useModal({
    component: VueFinalModal,
    attrs: {
      class: MODAL_CENTERED_CLASS,
      overlayTransition: 'vfm-fade',
      contentTransition: 'vfm-fade',
    },
    slots: { default: buildHomologationSlot(EMPTY_TARGET) },
  });

  const openEditHomologation = (target: RecordModalTarget) => {
    closeMenu();
    homologationModal.patchOptions({ slots: { default: buildHomologationSlot(target) } });
    return homologationModal.open();
  };

  const buildHistorySlot = (target: RecordModalTarget, entries: RecordChangelogEntry[]) =>
    useModalSlot({
      component: RecordHistoryModal,
      attrs: {
        target,
        entries,
        onClose: () => {
          historyModal.close();
        },
      },
    });

  const historyModal = useModal({
    component: VueFinalModal,
    attrs: buildDialogAttrs('Histórico das Atualizações (changelog)'),
    slots: { default: buildHistorySlot(EMPTY_TARGET, []) },
  });

  const isHistoryLoading = ref(false);

  /**
   * The two read only modals are opened by the row icons and not by the dropdown,
   * but the dropdown may well be open when the user reaches for an icon, so they
   * close it like every other action of the row does.
   *
   * Both fetch before the modal opens, and only open on success: a failed request must not show
   * a modal frame with nothing in it, so the toast is the only feedback.
   */
  const openHistory = async (target: RecordModalTarget) => {
    closeMenu();
    isHistoryLoading.value = true;

    try {
      const entries = await listRecordChangelog(target.id);
      historyModal.patchOptions({ slots: { default: buildHistorySlot(target, entries) } });
      await historyModal.open();
    } catch (error) {
      toast.showErrorToast({ title: RECORD_HISTORY_ERROR_MESSAGE, type: 'error' });
      console.error('Erro ao carregar o histórico do registro:', error);
    } finally {
      isHistoryLoading.value = false;
    }
  };

  const buildControlSlot = (target: RecordModalTarget, control: RecordControl) =>
    useModalSlot({
      component: RecordControlModal,
      attrs: {
        target,
        control,
        onClose: () => {
          controlModal.close();
        },
      },
    });

  const controlModal = useModal({
    component: VueFinalModal,
    attrs: buildDialogAttrs(
      'Controle de Elaboração, Aprovação, Verificação e Homologação de Registros'
    ),
    slots: { default: buildControlSlot(EMPTY_TARGET, EMPTY_CONTROL) },
  });

  const isControlLoading = ref(false);

  const openRecordEditor = (target: RecordModalTarget) => {
    closeMenu();
    return router.push({ name: 'EditarFormulario', params: { id: String(target.id) } });
  };

  const openRecordConfig = (target: RecordModalTarget) => {
    closeMenu();
    return router.push({
      name: 'GestaoRegistrosConfigurarRegistro',
      params: { id: String(target.id) },
    });
  };

  const openControl = async (target: RecordModalTarget) => {
    closeMenu();
    isControlLoading.value = true;

    try {
      const control = await getRecordControl(target.id);
      controlModal.patchOptions({ slots: { default: buildControlSlot(target, control) } });
      await controlModal.open();
    } catch (error) {
      toast.showErrorToast({ title: RECORD_CONTROL_ERROR_MESSAGE, type: 'error' });
      console.error('Erro ao carregar o controle do registro:', error);
    } finally {
      isControlLoading.value = false;
    }
  };

  const pendingDeletion = ref<RecordDeleteSelection>({ recordIds: [], revisionIds: [] });

  const pendingDeletionIds = () => [
    ...pendingDeletion.value.recordIds,
    ...pendingDeletion.value.revisionIds,
  ];

  const refreshAfterDeletion = () => {
    options.refresh().catch((error: unknown) => {
      console.error('Erro ao atualizar a listagem após a exclusão:', error);
    });
  };

  const { openDeleteConfirmationModal } = useBulkDelete<{ id: number }>({
    entityName: { singular: 'registro', plural: 'registros' },
    getConfirmTitle: () => buildRecordDeleteTitle(pendingDeletion.value),
    singleDeleteApi: deleteRecord,
    bulkDeleteApi: deleteRecords,
    onDeleteSuccess: refreshAfterDeletion,
    onStaleData: refreshAfterDeletion,
    removeFromSelection: () => undefined,
    selectedCount: () => pendingDeletionIds().length,
    selectedIds: pendingDeletionIds,
  });

  const openDeleteConfirmation = async (selection: RecordDeleteSelection) => {
    closeMenu();
    pendingDeletion.value = selection;
    await openDeleteConfirmationModal();
  };

  const openBulkDeleteConfirmation = () =>
    openDeleteConfirmation({
      recordIds: options.selectedRecordIds(),
      revisionIds: options.selectedRevisionIds(),
    });

  const togglingIds = ref<Set<number>>(new Set());

  const isToggling = (id: number) => togglingIds.value.has(id);

  const setToggling = (id: number, active: boolean) => {
    const next = new Set(togglingIds.value);
    if (active) next.add(id);
    else next.delete(id);
    togglingIds.value = next;
  };

  /**
   * The switch moves first and the request follows, because refetching the listing here made the
   * whole table blink through its skeleton for a change that touches one cell. A write that fails
   * puts the row back where it was and says so.
   */
  const toggleRecordActive = async (id: number, isActive: boolean) => {
    if (isToggling(id)) return;

    closeMenu();
    setToggling(id, true);

    const rollback = options.applyRecordStatus(id, isActive);

    try {
      await updateRecordStatus(id, isActive);
      toast.showSuccessToast({
        title: isActive ? 'Registro ativado com sucesso.' : 'Registro inativado com sucesso.',
        type: 'success',
      });
      options.settleRecordStatus(id);
    } catch (error) {
      rollback();
      toast.showErrorToast({ title: RECORD_STATUS_ERROR_MESSAGE, type: 'error' });
      console.error('Erro ao alterar o status do registro:', error);
    } finally {
      setToggling(id, false);
    }
  };

  return {
    openMenuKey,
    setOpenMenuKey,
    closeMenu,
    openEditHomologation,
    openRecordEditor,
    openRecordConfig,
    openHistory,
    isHistoryLoading,
    openControl,
    isControlLoading,
    openDeleteConfirmation,
    openBulkDeleteConfirmation,
    toggleRecordActive,
    isToggling,
  };
}
